
import React, { useState, useMemo, createContext, useContext, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { loadAuthFromStorage, selectToken } from './store/slices/authSlice';
import { marketingAPI } from './lib/marketing-api';
import { initWebPushRegistration } from './lib/firebase-push';
import { requestNotificationPermissionWhenLoggedIn } from './lib/notification-permission';
import { handleGlobalPaste } from './lib/paste-sanitizer';
import { LoginPage } from './pages/LoginPage';
import { SSORedirectPage } from './pages/SSORedirectPage';
import { RoleDashboardRouter } from './pages/dashboards/RoleDashboardRouter';
import { LeadsPage } from './pages/LeadsPage';
import { ContactsPage } from './pages/ContactsPage';
import { VisitingCardContactsPage } from './pages/VisitingCardContactsPage';
import { DomainsPage } from './pages/DomainsPage';
import { DomainFormPage } from './pages/DomainFormPage';
import { RegionFormPage } from './pages/RegionFormPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { ContactFormPage } from './pages/ContactFormPage';
import { LeadFormPage } from './pages/LeadFormPage';
import { CustomerFormPage } from './pages/CustomerFormPage';
import { OrdersPage } from './pages/OrdersPage';
import { OrderFormPage } from './pages/OrderFormPage';
import { EnquiryQuotationsPage } from './pages/EnquiryQuotationsPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { OrganizationFormPage } from './pages/OrganizationFormPage';
import { CustomersPage } from './pages/CustomersPage';
import { DatabaseLayout } from './components/layout/DatabaseLayout';
import { InventoryPage } from './pages/InventoryPage';
import { FinancialsPage } from './pages/FinancialsPage';
import { ReportsPage } from './pages/ReportsPage';
import { LeadsByRegionPage } from './pages/LeadsByRegionPage';
import { ExpectedOrderNewPage } from './pages/ExpectedOrderNewPage';
import { ODPlanPage } from './pages/ODPlanPage';
import { SettingsPage } from './pages/SettingsPage';
import { SupportTicketsPage } from './pages/SupportTicketsPage';
import { ServiceContractsPage } from './pages/ServiceContractsPage';
import { ServiceContractFormPage } from './pages/ServiceContractFormPage';
import { ServiceContractPlanPage } from './pages/ServiceContractPlanPage';
import { ServiceVisitsPage } from './pages/ServiceVisitsPage';
import { ServiceWorkOrderPage } from './pages/ServiceWorkOrderPage';
import { ServiceWorkOrdersPage } from './pages/ServiceWorkOrdersPage';
import { ServiceStorePage } from './pages/ServiceStorePage';
import { ServiceComplaintsPage } from './pages/ServiceComplaintsPage';
import { ServiceReportsPage } from './pages/ServiceReportsPage';
import { ServiceComplaintFormPage } from './pages/ServiceComplaintFormPage';
import { ServiceComplaintDetailPage } from './pages/ServiceComplaintDetailPage';
import { ServiceVisitReportPage } from './pages/ServiceVisitReportPage';
import { SchemaPage } from './pages/SchemaPage';
import { ReportTemplatesPage } from './pages/ReportTemplatesPage';
import { MyTeamPage } from './pages/MyTeamPage';
import { DSRPage } from './pages/DSRPage';
import { DailyServiceReportsPage } from './pages/DailyServiceReportsPage';
import { DailyServiceReportFormPage } from './pages/DailyServiceReportFormPage';
import { MyTodoPage } from './pages/MyTodoPage';
import { EventsListPage } from './pages/EventsListPage';
import { EventFormPage } from './pages/EventFormPage';
import { EventDetailPage } from './pages/EventDetailPage';
import { RolesPage } from './pages/RolesPage';
import { LiveActivityPage } from './pages/LiveActivityPage';
import { TooltipProvider } from './UI/Tooltip';
import { Toast, ToastType } from './components/ui/Toast';

const NumberingSeriesPage = lazy(() => import('./pages/NumberingSeriesPage').then(m => ({ default: m.NumberingSeriesPage })));
import { AppNotification } from './types';
import { ErrorBoundary } from './components/ui/ErrorBoundary';

interface AppContextType {
  showToast: (message: string, type?: ToastType) => void;
  globalSearch: string;
  setGlobalSearch: (val: string) => void;
  notifications: AppNotification[];
  setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
  unreadCount: number;
  markAsRead: (id: string | number) => void;
  markAllAsRead: () => void;
  loadNotifications: () => Promise<void>;
  toast: { message: string; type: ToastType } | null;
  onCloseToast: () => void;
  simulateDemo: () => void;
  clearDemo: () => void;
  isDemoActive: boolean;
  orders: any[];
  customers: any[];
}

const AppContext = createContext<AppContextType | undefined>(undefined);

/** Default no-op context so useApp() does not throw when used outside provider (e.g. wrong tree order). */
const defaultAppContext: AppContextType = {
  showToast: () => {},
  globalSearch: '',
  setGlobalSearch: () => {},
  notifications: [],
  setNotifications: () => {},
  unreadCount: 0,
  markAsRead: () => {},
  markAllAsRead: () => {},
  loadNotifications: async () => {},
  toast: null,
  onCloseToast: () => {},
  simulateDemo: () => {},
  clearDemo: () => {},
  isDemoActive: false,
  orders: [],
  customers: [],
};

export const useApp = () => {
  const context = useContext(AppContext);
  return context ?? defaultAppContext;
};

const INITIAL_NOTIFICATIONS: AppNotification[] = [
  { id: '1', title: 'New Order Received', message: 'Order #ORD-7237 processed for Sarah Jenkins.', time: '2m ago', type: 'order', read: false },
  { id: '2', title: 'System Security Alert', message: 'New login detected from Austin, TX.', time: '15m ago', type: 'system', read: false },
  { id: '3', title: 'Inventory Warning', message: 'Premium ERP License stock is below 15%.', time: '1h ago', type: 'inventory', read: false },
  { id: '4', title: 'Payment Confirmed', message: 'Payment of $1,200.00 confirmed from Studio Hub.', time: '3h ago', type: 'system', read: false },
  { id: '5', title: 'Customer Feedback', message: 'Alice Thompson rated the support experience 5/5.', time: '5h ago', type: 'customer', read: true },
];

function formatNotificationTime(createdAt: string): string {
  try {
    const d = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  } catch {
    return '';
  }
}

const AppMain: React.FC = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);
  const [globalSearch, setGlobalSearch] = useState('');
  const [notifications, setNotifications] = useState<AppNotification[]>(INITIAL_NOTIFICATIONS);
  const [orders, setOrders] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const loadNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const list = await marketingAPI.getNotifications(false, 50);
      const mapped: AppNotification[] = list.map((n) => ({
        id: n.id,
        title: n.title,
        message: n.message || '',
        time: formatNotificationTime(n.created_at),
        type: (n.notification_type === 'follow_up' ? 'follow_up' : n.notification_type === 'new_inquiry' ? 'new_inquiry' : 'system') as AppNotification['type'],
        read: !!n.read_at,
        link: n.link || (n.lead_id != null ? `/leads/${n.lead_id}/edit` : undefined),
        lead_id: n.lead_id ?? undefined,
      }));
      setNotifications(mapped);
    } catch {
      // Keep existing state on error
    }
  }, [token]);

  useEffect(() => {
    dispatch(loadAuthFromStorage());
  }, [dispatch]);

  // Sanitize pasted "fancy text" (styled Unicode look-alikes) app-wide so it always
  // renders in the app's own font, no matter what field it's pasted into.
  useEffect(() => {
    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, []);

  useEffect(() => {
    if (token) loadNotifications();
    else setNotifications(INITIAL_NOTIFICATIONS);
  }, [token, loadNotifications]);

  useEffect(() => {
    if (!token) return;
    requestNotificationPermissionWhenLoggedIn();
    initWebPushRegistration().catch(() => {});
  }, [token]);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    setToast({ message, type });
  }, []);

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const markAsRead = (id: string | number) => {
    if (typeof id === 'number') {
      marketingAPI.markNotificationRead(id).then(() => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      }).catch(() => {});
    } else {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    }
  };

  const markAllAsRead = () => {
    if (token) {
      marketingAPI.markAllNotificationsRead().then(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        showToast('All notifications marked as read');
      }).catch(() => {});
    } else {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      showToast('All notifications marked as read');
    }
  };

  const [isDemoActive, setIsDemoActive] = useState(false);

  const simulateDemo = () => {
    import('./demoData').then(data => {
      setNotifications(prev => [...data.DEMO_NOTIFICATIONS, ...prev]);
      setOrders(data.DEMO_ORDERS);
      setCustomers(data.DEMO_CUSTOMERS);
      setIsDemoActive(true);
      showToast('System-wide demo data simulated', 'success');
    });
  };

  const clearDemo = () => {
    setNotifications(INITIAL_NOTIFICATIONS);
    setOrders([]);
    setCustomers([]);
    setIsDemoActive(false);
    showToast('Demo data flushed from system', 'info');
  };

  const contextValue = useMemo(() => ({
    showToast,
    globalSearch,
    setGlobalSearch,
    notifications,
    setNotifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    loadNotifications,
    toast,
    onCloseToast: () => setToast(null),
    simulateDemo,
    clearDemo,
    isDemoActive,
    orders,
    customers
  }), [globalSearch, notifications, unreadCount, toast, isDemoActive, orders, customers, loadNotifications]);

  return (
    <AppContext.Provider value={contextValue}>
      <TooltipProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login-redirect" element={<SSORedirectPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<RoleDashboardRouter />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="database" element={<DatabaseLayout />}>
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="visiting-card-contacts" element={<VisitingCardContactsPage />} />
              </Route>
              <Route path="domains" element={<DomainsPage />} />
              <Route path="domains/new" element={<DomainFormPage />} />
              <Route path="domains/:id/edit" element={<DomainFormPage />} />
              <Route path="domains/:domainId/regions/new" element={<RegionFormPage />} />
              <Route path="domains/:domainId/regions/:regionId/edit" element={<RegionFormPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="contacts/new" element={<ContactFormPage />} />
              <Route path="contacts/:id/edit" element={<ContactFormPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="leads/new" element={<LeadFormPage />} />
              <Route path="leads/:id/edit" element={<LeadFormPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/new" element={<CustomerFormPage />} />
              <Route path="customers/:id/edit" element={<CustomerFormPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/new" element={<OrderFormPage />} />
              <Route path="orders/:id" element={<OrderFormPage />} />
              <Route path="quotations" element={<EnquiryQuotationsPage />} />
              <Route path="organizations" element={<OrganizationsPage />} />
              <Route path="organizations/new" element={<OrganizationFormPage />} />
              <Route path="organizations/:id/edit" element={<OrganizationFormPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="financials" element={<FinancialsPage />} />
              <Route path="events" element={<EventsListPage />} />
              <Route path="events/new" element={<EventFormPage />} />
              <Route path="events/:id" element={<EventDetailPage />} />
              <Route path="events/:id/edit" element={<EventFormPage />} />
              <Route path="my-team" element={<MyTeamPage />} />
              <Route path="dsr" element={<DSRPage />} />
              <Route path="daily-service-reports" element={<DailyServiceReportsPage />} />
              <Route path="daily-service-reports/new" element={<DailyServiceReportFormPage />} />
              <Route path="daily-service-reports/:id/edit" element={<DailyServiceReportFormPage />} />
              <Route path="daily-service-reports/expense/:id/edit" element={<DailyServiceReportFormPage />} />
              <Route path="my-todo" element={<MyTodoPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="report-templates" element={<ReportTemplatesPage />} />
              <Route path="reports/expected-order/new" element={<ExpectedOrderNewPage />} />
              <Route path="reports/od-plan" element={<ODPlanPage />} />
              <Route path="reports/leads-by-region" element={<LeadsByRegionPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="support" element={<SupportTicketsPage />} />
              <Route path="service" element={<Navigate to="/service/contracts" replace />} />
              <Route path="service/contracts" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceContractsPage />
                </ProtectedRoute>
              } />
              <Route path="service/contracts/new" element={
                <ProtectedRoute requiredPermission="service.create_contract">
                  <ServiceContractFormPage />
                </ProtectedRoute>
              } />
              <Route path="service/contracts/:id/edit" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceContractFormPage />
                </ProtectedRoute>
              } />
              <Route path="service/contracts/:id/plan" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceContractPlanPage />
                </ProtectedRoute>
              } />
              <Route path="service/visits" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceVisitsPage />
                </ProtectedRoute>
              } />
              <Route path="service/contracts/:contractId/work-order" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceWorkOrderPage />
                </ProtectedRoute>
              } />
              <Route path="service/visits/:visitId/work-order" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceWorkOrderPage />
                </ProtectedRoute>
              } />
              <Route path="service/visits/:visitId/report" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceVisitReportPage />
                </ProtectedRoute>
              } />
              <Route path="service/work-orders" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceWorkOrdersPage />
                </ProtectedRoute>
              } />
              <Route path="service/reports" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceReportsPage />
                </ProtectedRoute>
              } />
              <Route path="service/store" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceStorePage />
                </ProtectedRoute>
              } />
              <Route path="service/complaints" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceComplaintsPage />
                </ProtectedRoute>
              } />
              <Route path="service/complaints/new" element={
                <ProtectedRoute requiredPermission="service.create_complaint">
                  <ServiceComplaintFormPage />
                </ProtectedRoute>
              } />
              <Route path="service/complaints/:id" element={
                <ProtectedRoute requiredPermission="service.view">
                  <ServiceComplaintDetailPage />
                </ProtectedRoute>
              } />
              <Route path="numbering-series" element={<Suspense fallback={<div className="p-8 text-center text-slate-500">Loading...</div>}><NumberingSeriesPage /></Suspense>} />
              <Route path="numbering-series/new" element={<Suspense fallback={<div className="p-8 text-center text-slate-500">Loading...</div>}><NumberingSeriesPage /></Suspense>} />
              <Route path="numbering-series/:id/edit" element={<Suspense fallback={<div className="p-8 text-center text-slate-500">Loading...</div>}><NumberingSeriesPage /></Suspense>} />
              <Route path="roles" element={<RolesPage />} />
              <Route path="live-activity" element={
                <ProtectedRoute requiredPermission="presence.view_users">
                  <LiveActivityPage />
                </ProtectedRoute>
              } />
              <Route path="schema" element={<SchemaPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </TooltipProvider>
    </AppContext.Provider>
  );
};

const App: React.FC = () => (
  <ErrorBoundary>
    <AppMain />
  </ErrorBoundary>
);

export default App;
